import type { ApiResponse } from '@/types'
import { logger } from '@/utils/logger'

const BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'
const NETWORK_ERROR_CODE = 'NETWORK_ERROR'
const NETWORK_ERROR_MESSAGE = '网络连接异常，请检查网络后重试'

/** 租户生命周期错误码（HTTP 403），需要走未授权处理路径跳转到暂停页。 */
const TENANT_LIFECYCLE_ERROR_CODES: ReadonlySet<string> = new Set([
  'TENANT_SUSPENDED',
  'TENANT_EXPIRED'
])

function isTenantLifecycleErrorCode(body: unknown): boolean {
  if (body && typeof body === 'object' && 'code' in body) {
    const code = (body as { code: unknown }).code
    return typeof code === 'string' && TENANT_LIFECYCLE_ERROR_CODES.has(code)
  }
  return false
}

type AuthTokenResolver = () => string | null
type TokenRefreshAttempter = () => Promise<boolean>
type UnauthorizedHandler = (error: HttpError) => void | Promise<void>
type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
type ResponseType = 'json' | 'text' | 'blob'

export type HttpRequestOptions = {
  skipUnauthorizedHandler?: boolean
  /**
   * Do not attach the active access token. Use only for anonymous endpoints
   * that establish or restore a session, never as an authorization bypass.
   */
  skipAuth?: boolean
}

type RequestConfig = {
  url: string
  method: HttpMethod
  params?: Record<string, unknown>
  body?: unknown
  options?: HttpRequestOptions
}

let authTokenResolver: AuthTokenResolver = () => null
let tokenRefreshAttempter: TokenRefreshAttempter | null = null
let unauthorizedHandler: UnauthorizedHandler | null = null
let activeRefreshPromise: Promise<boolean> | null = null

export class HttpError extends Error {
  status: number
  code: string
  traceId: string
  // 后端返回的 errors 形态多样：
  //  - 字段级：{ fieldName: ['msg1','msg2'] }
  //  - 字符串数组：['msg1','msg2']
  //  - 结构化对象数组（DomainValidationException/校验失败）：[{code,field,message,row}]
  errors?: Record<string, string[]> | string[] | Array<Record<string, unknown>>

  constructor(options: {
    message: string
    status: number
    code: string
    traceId?: string
    errors?: Record<string, string[]> | string[] | Array<Record<string, unknown>>
    cause?: unknown
  }) {
    super(options.message, { cause: options.cause })
    this.name = 'HttpError'
    this.status = options.status
    this.code = options.code
    this.traceId = options.traceId ?? ''
    this.errors = options.errors
  }
}

export function setAuthTokenResolver(resolver: AuthTokenResolver): void {
  authTokenResolver = resolver
}

export function setTokenRefreshAttempter(attempter: TokenRefreshAttempter): void {
  tokenRefreshAttempter = attempter
}

export function setUnauthorizedHandler(handler: UnauthorizedHandler): void {
  unauthorizedHandler = handler
}

async function attemptTokenRefresh(): Promise<boolean> {
  if (!tokenRefreshAttempter) return false

  if (activeRefreshPromise) return activeRefreshPromise

  activeRefreshPromise = tokenRefreshAttempter().catch(() => false)
  try {
    return await activeRefreshPromise
  } finally {
    activeRefreshPromise = null
  }
}

export function buildApiUrl(
  url: string,
  params?: Record<string, unknown>,
  baseUrl = BASE_URL
): string {
  const fullUrl = new URL(`${baseUrl}${url}`, window.location.origin)

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        fullUrl.searchParams.append(key, String(value))
      }
    })
  }

  return fullUrl.toString()
}

export function buildAuthHeaders(headers: Record<string, string> = {}): HeadersInit {
  const resolvedHeaders = { ...headers }
  const token = authTokenResolver()
  if (token) {
    resolvedHeaders.Authorization = `Bearer ${token}`
  }
  return resolvedHeaders
}

export async function runUnauthorizedHandler(
  error: HttpError,
  options: HttpRequestOptions = {}
): Promise<void> {
  if (error.status === 401 && unauthorizedHandler && !options.skipUnauthorizedHandler) {
    await unauthorizedHandler(error)
  }
}

class HttpClient {
  private readonly baseURL: string

  constructor(baseURL: string) {
    this.baseURL = baseURL
  }

  private getHeaders(hasBody = false, includeAuthorization = true): HeadersInit {
    const headers: Record<string, string> = {}

    if (hasBody) {
      headers['Content-Type'] = 'application/json'
    }

    if (includeAuthorization) {
      const token = authTokenResolver()
      if (token) {
        headers.Authorization = `Bearer ${token}`
      }
    }

    return headers
  }

  private async parseBody(response: Response): Promise<unknown> {
    if (response.status === 204) {
      return null
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return null
    }

    try {
      return await response.json()
    } catch (error) {
      logger.warn('[API] JSON 解析失败:', error)
      return null
    }
  }

  private buildSuccessResponse<T>(response: Response, body: unknown): ApiResponse<T> {
    if (
      body &&
      typeof body === 'object' &&
      'data' in body &&
      'code' in body &&
      'message' in body
    ) {
      return body as ApiResponse<T>
    }

    return {
      success: true,
      code: 'SUCCESS',
      message: 'OK',
      data: (body ?? undefined) as T,
      traceId: response.headers.get('x-trace-id') || '',
      timestamp: new Date().toISOString()
    }
  }

  private buildHttpError(response: Response, body: unknown): HttpError {
    const fallbackMessage = response.status === 401 ? '登录状态已失效，请重新登录' : '请求失败'

    if (body && typeof body === 'object') {
      const responseBody = body as Partial<ApiResponse<unknown>>

      return new HttpError({
        message: typeof responseBody.message === 'string' ? responseBody.message : fallbackMessage,
        status: response.status,
        code: typeof responseBody.code === 'string' ? responseBody.code : String(response.status),
        traceId: typeof responseBody.traceId === 'string' ? responseBody.traceId : '',
        errors: responseBody.errors
      })
    }

    return new HttpError({
      message: fallbackMessage,
      status: response.status,
      code: String(response.status)
    })
  }

  private buildNetworkError(cause: unknown): HttpError {
    const message =
      cause instanceof Error && cause.message.trim().length > 0
        ? cause.message
        : NETWORK_ERROR_MESSAGE

    return new HttpError({
      message,
      status: 0,
      code: NETWORK_ERROR_CODE,
      cause
    })
  }

  private async handleErrorResponse(
    response: Response,
    options: HttpRequestOptions = {}
  ): Promise<never> {
    const body = await this.parseBody(response)
    const error = this.buildHttpError(response, body)

    // 401 视为未授权（需重新登录）；403 且错误码为租户生命周期错误时视为租户不可用（跳暂停页）
    const isAuthError = response.status === 401
      || (response.status === 403 && isTenantLifecycleErrorCode(body))
    if (isAuthError && unauthorizedHandler && !options.skipUnauthorizedHandler) {
      await unauthorizedHandler(error)
    }

    throw error
  }

  private async handleJsonResponse<T>(
    response: Response,
    options: HttpRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    if (!response.ok) {
      return this.handleErrorResponse(response, options)
    }

    const body = await this.parseBody(response)
    return this.buildSuccessResponse<T>(response, body)
  }

  private async handleBlobResponse(
    response: Response,
    options: HttpRequestOptions = {}
  ): Promise<Blob> {
    if (!response.ok) {
      return this.handleErrorResponse(response, options)
    }
    return response.blob()
  }

  private async handleTextResponse(
    response: Response,
    options: HttpRequestOptions = {}
  ): Promise<string> {
    if (!response.ok) {
      return this.handleErrorResponse(response, options)
    }

    return response.text()
  }

  private buildUrl(url: string, params?: Record<string, unknown>): string {
    return buildApiUrl(url, params, this.baseURL)
  }

  private createRequestInit(
    method: HttpMethod,
    body?: unknown,
    options?: HttpRequestOptions
  ): RequestInit {
    const hasBody = body !== undefined

    return {
      method,
      headers: this.getHeaders(hasBody, !options?.skipAuth),
      body: hasBody ? JSON.stringify(body) : undefined
    }
  }

  private async send(url: string, init: RequestInit): Promise<Response> {
    try {
      return await fetch(url, init)
    } catch (cause) {
      throw this.buildNetworkError(cause)
    }
  }

  private async request<T>(
    config: RequestConfig,
    responseType: 'json'
  ): Promise<ApiResponse<T>>
  private async request(config: RequestConfig, responseType: 'text'): Promise<string>
  private async request(config: RequestConfig, responseType: 'blob'): Promise<Blob>
  private async request<T>(
    config: RequestConfig,
    responseType: ResponseType
  ): Promise<ApiResponse<T> | string | Blob> {
    let response = await this.send(
      this.buildUrl(config.url, config.params),
      this.createRequestInit(config.method, config.body, config.options)
    )

    if (response.status === 401 && !config.options?.skipUnauthorizedHandler) {
      // 安全审查 M4 修复：只有后端显式返回 TOKEN_EXPIRED 时才尝试续签 token。
      // 其它 401（如账号被禁用/锁定/未登录）应直接交给 unauthorizedHandler
      // 跳转登录页，而不是用 refresh 反复失败浪费时间。
      // 注意：Response body 只能读一次，解析后必须复用，不能再次 parseBody。
      const firstBody = await this.parseBody(response)
      let error = this.buildHttpError(response, firstBody)
      const isTokenExpired = error.code === 'TOKEN_EXPIRED'

      if (isTokenExpired) {
        const refreshed = await attemptTokenRefresh()
        if (refreshed) {
          response = await this.send(
            this.buildUrl(config.url, config.params),
            this.createRequestInit(config.method, config.body, config.options)
          )
          if (response.status === 401) {
            const retryBody = await this.parseBody(response)
            error = this.buildHttpError(response, retryBody)
          }
        }
      }

      if (response.status === 401) {
        if (unauthorizedHandler) {
          await unauthorizedHandler(error)
        }
        throw error
      }
    }

    if (responseType === 'text') {
      return this.handleTextResponse(response, config.options)
    }

    if (responseType === 'blob') {
      return this.handleBlobResponse(response, config.options)
    }

    return this.handleJsonResponse<T>(response, config.options)
  }

  async get<T>(
    url: string,
    params?: Record<string, unknown>,
    options: HttpRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'GET', params, options }, 'json')
  }

  async getText(
    url: string,
    params?: Record<string, unknown>,
    options: HttpRequestOptions = {}
  ): Promise<string> {
    return this.request({ url, method: 'GET', params, options }, 'text')
  }

  async getBlob(
    url: string,
    params?: Record<string, unknown>,
    options: HttpRequestOptions = {}
  ): Promise<Blob> {
    return this.request({ url, method: 'GET', params, options }, 'blob')
  }

  async post<T>(
    url: string,
    body?: unknown,
    options: HttpRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'POST', body, options }, 'json')
  }

  /** multipart 上传（FormData；浏览器自动生成 boundary，不手动设 Content-Type） */
  async postForm<T>(
    url: string,
    form: FormData,
    options: HttpRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    const headers = this.getHeaders(false, !options.skipAuth) as Record<string, string>
    const response = await this.send(this.buildUrl(url), {
      method: 'POST',
      headers,
      body: form
    })
    return this.handleJsonResponse<T>(response, options)
  }

  async put<T>(
    url: string,
    body?: unknown,
    options: HttpRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'PUT', body, options }, 'json')
  }

  async patch<T>(
    url: string,
    body?: unknown,
    options: HttpRequestOptions = {}
  ): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'PATCH', body, options }, 'json')
  }

  async delete<T>(url: string, options: HttpRequestOptions = {}): Promise<ApiResponse<T>> {
    return this.request<T>({ url, method: 'DELETE', options }, 'json')
  }

  async postBlob(
    url: string,
    body?: unknown,
    options: HttpRequestOptions = {}
  ): Promise<Blob> {
    return this.request({ url, method: 'POST', body, options }, 'blob')
  }
}

export const http = new HttpClient(BASE_URL)
